import Cookies from 'js-cookie';
import { getCookieRootDomain } from '@/utils/config';
import { getTokenExpirationDate } from '@/utils/jwt';
import { accessTokenCookieName, languageCookieName } from '@/constants/common';

export function setCookie(name: string, value: string, options: Cookies.CookieAttributes) : string | undefined {
  return Cookies.set(name, value, options);
}

export function getCookie(name: string) : string | undefined {
  return Cookies.get(name);
}

export function removeCookie(name: string, options: Cookies.CookieAttributes) : void {
  Cookies.remove(name, options);
}

export function setAccessCookie(value: string) : string | undefined {
  const domain = getCookieRootDomain();
  const expirationDate = getTokenExpirationDate(value);

  if (expirationDate === null) {
    return;
  }

  return setCookie(accessTokenCookieName, value, {
    domain,
    secure: true,
    sameSite: 'strict',
    expires: expirationDate
  });
}

export function getAccessCookie() : string | undefined {
  return getCookie(accessTokenCookieName);
}

export function removeAccessCookie() : void {
  const domain = getCookieRootDomain();

  removeCookie(accessTokenCookieName, {
    domain,
    sameSite: 'strict',
    secure: true
  });
}

function getExpirationDate(days = 365) : Date | null {
  const expirationDate = new Date();

  expirationDate.setTime(expirationDate.getTime() + (days * 24 * 60 * 60 * 1000));

  return expirationDate;
}

export function setSessionCookie(name: string, value: string) : string | undefined {
  const domain = getCookieRootDomain();
  const isSecure = window.location.protocol === 'https:';

  return setCookie(name, value, {
    session: true,
    domain,
    secure: isSecure,
    sameSite: 'lax'
  });
}

export function setExpiringCookie(name: string, value: string, days = 365) : string | undefined {
  const domain = getCookieRootDomain();
  const expirationDate = getExpirationDate(days);
  const isSecure = window.location.protocol === 'https:';

  if (expirationDate === null) {
    return;
  }

  return setCookie(name, value, {
    domain,
    secure: isSecure,
    sameSite: 'lax',
    expires: expirationDate
  });
}

export interface SetExpiringCookieWithDomainOptions {
  readonly name: string;
  readonly value: string;
  readonly days?: number;
  readonly domain: string;
}

/**
 * Same expiry semantics as {@link setExpiringCookie}, but with an explicit `domain` (used for pre-release dual writes).
 */
export function setExpiringCookieWithDomain(options: SetExpiringCookieWithDomainOptions) : string | undefined {
  const { name, value, domain, days } = options;
  const expirationDate = getExpirationDate(days ?? 365);
  const isSecure = window.location.protocol === 'https:';

  if (expirationDate === null) {
    return;
  }

  return setCookie(name, value, {
    domain,
    secure: isSecure,
    sameSite: 'lax',
    expires: expirationDate
  });
}

export interface SetExpiringCookieWithDomainUntilOptions {
  readonly name: string;
  readonly value: string;
  readonly expirationDate: Date;
  readonly domain: string;
}

/** Absolute-expiry counterpart to {@link setExpiringCookieWithDomain}. */
export function setExpiringCookieWithDomainUntil(
  options: SetExpiringCookieWithDomainUntilOptions
) : string | undefined {
  const { name, value, expirationDate, domain } = options;
  const isSecure = window.location.protocol === 'https:';

  return setCookie(name, value, {
    domain,
    secure: isSecure,
    sameSite: 'lax',
    expires: expirationDate
  });
}

/** Sets a non-auth cookie with one absolute expiry shared by every participant. */
export function setExpiringCookieUntil(
  name: string,
  value: string,
  expirationDate: Date
) : string | undefined {
  return setExpiringCookieWithDomainUntil({
    name,
    value,
    expirationDate,
    domain: getCookieRootDomain()
  });
}

export function setLanguageCookie(value: string) : string | undefined {
  return setCookie(languageCookieName, value, {
    domain: getCookieRootDomain(),
    secure: window.location.protocol === 'https:',
    sameSite: 'lax'
  });
}

export function getLanguageCookie() : string | undefined {
  return getCookie(languageCookieName);
}
